// @flow

import React, { useMemo } from 'react';
import type { Element } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { faAngleRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';

import { noop } from 'lodash';

import { withTranslation } from '@i18n';

import type { GlobalState } from '@globalTypes';
import type { FilterProps, FilterOption } from '../types';

import { setActiveFilter, setFilterValue, goBack } from '../redux/actions';

import Item from './Item';

import {
  StyledFilterWrapper,
  StyledFilterBody,
  StyledFilterHeader,
  StyledFilterValue,
  StyledFilterIcon,
  StyledFilterCollapsed,
  StyledFilterStatus,
  StyledFilterLabel,
  StyledFilterBackButton,
  StyledFilterHeaderLabel
} from '../styles';

const Select = ({
  label,
  value: filterValue,
  isActive,
  filterKey,
  options,
  setFilterValue,
  setActiveFilter,
  goBack,
  t
}: FilterProps): Element<any> => {
  const valueLabel: ?string = useMemo(() => {
    const selectedOption: ?FilterOption = options.find(option => option.value === filterValue);

    return selectedOption?.label;
  }, [options, filterValue]);

  const setActive = (): void => {
    setActiveFilter(filterKey);
  };

  const setValue = (_, value): void => {
    setFilterValue(filterKey, value);
  };

  return (
    <StyledFilterWrapper onClick={isActive ? noop : setActive} isActive={isActive}>
      {!isActive && (
        <StyledFilterCollapsed>
          <StyledFilterStatus>
            <StyledFilterLabel>{label}</StyledFilterLabel>

            <StyledFilterValue>{valueLabel || t('MobileFilters.All')}</StyledFilterValue>
          </StyledFilterStatus>

          <StyledFilterIcon icon={faAngleRight} />
        </StyledFilterCollapsed>
      )}

      {isActive && (
        <>
          <StyledFilterHeader>
            <StyledFilterBackButton icon={faChevronLeft} onClick={goBack} />
            <StyledFilterHeaderLabel>{label}</StyledFilterHeaderLabel>
          </StyledFilterHeader>

          <StyledFilterBody>
            {options.map(({ label, value, subLabel }: FilterOption) => (
              <Item
                key={value}
                isActive={filterValue === value}
                label={label}
                value={value}
                subLabel={subLabel}
                onClick={setValue}
              />
            ))}
          </StyledFilterBody>
        </>
      )}
    </StyledFilterWrapper>
  );
};

const mapStateToProps = ({ mobileFilters }: GlobalState, { filterKey }: FilterProps) => ({
  isActive: mobileFilters?.currentFilter === filterKey,
  value: mobileFilters?.values?.[filterKey]
});

const mapDispatchToProps = {
  setActiveFilter,
  setFilterValue,
  goBack
};

export default compose(
  withTranslation('common'),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(Select);
